import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useTwa } from '@/app/TwaProvider';

export type MainButtonOptions = {
  text: string;
  onClick: () => void;
  visible?: boolean;
  enabled?: boolean;
  loading?: boolean;
  color?: string;
  textColor?: string;
};

type MainButtonStore = {
  state: MainButtonOptions | null;
  set: (s: MainButtonOptions | null) => void;
};

export const useMainButtonStore = create<MainButtonStore>((set) => ({
  state: null,
  set: (s) => set({ state: s }),
}));

/**
 * useMainButton drives the Telegram WebApp.MainButton when running inside
 * Telegram. In a browser, BrowserMainButton reads the same state and renders
 * a fixed-bottom CTA so web users still see the action.
 */
export function useMainButton(options: MainButtonOptions) {
  const { webApp, isTelegram } = useTwa();
  const setState = useMainButtonStore((s) => s.set);

  // Keep the latest onClick reachable without re-registering the Telegram
  // listener (and without forcing callers to wrap their handler in useCallback).
  // The Telegram-side `handler` reads through this ref each invocation, so
  // changes to `options.onClick` take effect on the next tap.
  const onClickRef = useRef(options.onClick);
  onClickRef.current = options.onClick;

  useEffect(() => {
    setState(options);
    return () => setState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.text,
    options.visible,
    options.enabled,
    options.loading,
    options.color,
    options.textColor,
    options.onClick,
  ]);

  useEffect(() => {
    if (!isTelegram || !webApp?.MainButton) return;
    const btn = webApp.MainButton;
    const handler = () => onClickRef.current();
    btn.setParams({
      text: options.text,
      color: options.color,
      text_color: options.textColor,
      is_active: options.enabled !== false && !options.loading,
      is_visible: options.visible !== false,
    });
    btn.onClick(handler);
    return () => {
      btn.offClick(handler);
      btn.hide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTelegram, webApp, options.text, options.visible, options.enabled, options.loading]);
}
